import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useState, useMemo } from "react";
import RefreshIcon from "@mui/icons-material/Refresh.js";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded.js";
import { ListItem, ListItemSecondaryAction, CircularProgress } from "@mui/material";
import StyledTooltip from "../../../../../components/styled-tooltip/styled-tooltip.js";
import { useAxiosProvider } from "../AxiosProvider.js";
import { canSendSMS } from "../../helpers.js";
import { useRecollectAnamnesis } from "../../hooks/useRecollectAnamnesis.js";
import { useRecollectFactoringAgreement } from "../../hooks/useRecollectFactoringAgreement.js";
import { useLabels } from "../../labels.provider.js";
import { DocumentStatus } from "../../patient-today-list.types.js";
import { formatDefault } from "../../../../../utils/date/formatDefault.js";
import { StatusNotDoneIcon, StatusDoneIcon, StyledPopover, StyledPopoverContent, StyledList, StyledListSubheader, StyledListItemIcon, ListItemContentWrapper, ListItemTextWrapper, StyledLink, StyledListItemText, Time, StyledIconButton, StyledButton, StyledListItemButton, ListItemButtonText, StyledMessageBubbleIcon, SmsSentText, SmsSentIcon, SendSmsAgainButton, StyledTabletIcon, GeneratedCodeListItem, GeneratedCodeText } from "./anamnesis-status-popover.styles.js";
import "axios";
import "libphonenumber-js/mobile";
import "@tanstack/react-query";
import "../../query-key-provider.js";
import "../../../../../constants/date/date.js";
import "../../../../../utils/date/format.js";
import "date-fns";
import "../../../../../utils/fp/flow/flow.js";
import "../../../../../utils/date/toDate.js";
import "date-fns/fp/parseISO";
import "@mui/icons-material/CheckRounded.js";
import "@mui/icons-material/CloseRounded.js";
import "@mui/material/styles";
import "../../../../../components/icons/MessageBubble/MessageBubble.js";
import "../../../../../components/icons/MainIcon/MainIcon.js";
import "../../../../../components/icons/TabletIcon/TabletIcon.js";
const iconByStatus = {
  [DocumentStatus.INVALID]: StatusNotDoneIcon,
  [DocumentStatus.MISSING]: StatusNotDoneIcon,
  [DocumentStatus.IN_PROGRESS]: StatusNotDoneIcon,
  [DocumentStatus.DONE]: StatusDoneIcon
};
const AnamnesisStatusPopover = ({
  anchorEl,
  onClose,
  anamnesis,
  factoring,
  patientReferenceId,
  patientPhoneNumber,
  onGenerateCode,
  onSendSms,
  generatedCode,
  isSmsSent,
  loading,
  isDocumentActionsActive
}) => {
  const { auth, anaUrl } = useAxiosProvider();
  const { labels, language } = useLabels();
  const { recollectAnamnesis } = useRecollectAnamnesis();
  const { recollectFactoringAgreement } = useRecollectFactoringAgreement();
  const [showRecollectAnamnesisConfirm, setShowRecollectAnamnesisConfirm] = useState(false);
  const [showRecollectFactoringConfirm, setShowRecollectFactoringConfirm] = useState(false);
  const AnamnesisIcon = useMemo(() => iconByStatus[anamnesis.status], [anamnesis.status]);
  const FactoringIcon = useMemo(() => iconByStatus[factoring.status], [factoring.status]);
  const anamnesisUrl = anamnesis.status === DocumentStatus.DONE && anamnesis.referenceId && auth && anaUrl ? `${anaUrl}?jwt=${auth}#/anamnesis/${anamnesis.referenceId}?source=${encodeURIComponent(
    window.location.href
  )}&lang=${language}` : null;
  const showAnamnesisRecollectIcon = anamnesis.status === DocumentStatus.DONE && !showRecollectAnamnesisConfirm;
  const showFactoringRecollectIcon = factoring.status === DocumentStatus.DONE && !showRecollectFactoringConfirm;
  const onRecollectAnamnesis = async () => {
    setShowRecollectAnamnesisConfirm(false);
    await recollectAnamnesis(patientReferenceId);
  };
  const onRecollectFactoringAgreement = async () => {
    setShowRecollectFactoringConfirm(false);
    await recollectFactoringAgreement(patientReferenceId);
  };
  const areDocumentsCompleted = anamnesis.status === DocumentStatus.DONE && factoring.status === DocumentStatus.DONE;
  return /* @__PURE__ */ jsx(
    StyledPopover,
    {
      anchorEl,
      keepMounted: true,
      open: Boolean(anchorEl),
      onClose,
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "center"
      },
      transformOrigin: {
        vertical: "top",
        horizontal: "left"
      },
      children: /* @__PURE__ */ jsxs(StyledPopoverContent, { children: [
        /* @__PURE__ */ jsxs(
          StyledList,
          {
            subheader: /* @__PURE__ */ jsx(StyledListSubheader, { disableGutters: true, disableSticky: true, children: labels.anamnesis.formStatus }),
            dense: true,
            children: [
              /* @__PURE__ */ jsxs(ListItem, { disableGutters: true, "data-testid": "anamnesis-step-ANAMNESIS", children: [
                /* @__PURE__ */ jsx(StyledListItemIcon, { children: /* @__PURE__ */ jsx(AnamnesisIcon, { "data-testid": "anamnesis-status-icon" }) }),
                /* @__PURE__ */ jsxs(ListItemContentWrapper, { isHidden: showRecollectAnamnesisConfirm, children: [
                  /* @__PURE__ */ jsx(ListItemTextWrapper, { children: anamnesisUrl ? /* @__PURE__ */ jsx(StyledLink, { href: anamnesisUrl, target: "_blank", children: labels.anamnesis.steps.anamnesis }) : /* @__PURE__ */ jsx(StyledListItemText, { variant: "Lato Emphasized 2", children: labels.anamnesis.steps.anamnesis }) }),
                  anamnesis.filledAt && /* @__PURE__ */ jsx(Time, { variant: "Lato Caption", children: formatDefault(anamnesis.filledAt) })
                ] }),
                /* @__PURE__ */ jsxs(ListItemSecondaryAction, { children: [
                  showAnamnesisRecollectIcon && /* @__PURE__ */ jsx(StyledTooltip, { title: labels.anamnesis.retriggerDocument, children: /* @__PURE__ */ jsx(StyledIconButton, { onClick: () => setShowRecollectAnamnesisConfirm(true), "data-testid": "recollect-btn", children: /* @__PURE__ */ jsx(RefreshIcon, {}) }) }),
                  showRecollectAnamnesisConfirm && /* @__PURE__ */ jsxs(Fragment, { children: [
                    /* @__PURE__ */ jsx(StyledButton, { onClick: () => setShowRecollectAnamnesisConfirm(false), children: labels.anamnesis.recollectCancel }),
                    /* @__PURE__ */ jsx(StyledButton, { contained: true, onClick: onRecollectAnamnesis, children: labels.anamnesis.recollectConfirm })
                  ] })
                ] })
              ] }),
              /* @__PURE__ */ jsxs(ListItem, { disableGutters: true, "data-testid": "anamnesis-step-FACTORING_AGREEMENT", children: [
                /* @__PURE__ */ jsx(StyledListItemIcon, { children: /* @__PURE__ */ jsx(FactoringIcon, { "data-testid": "factoring-status-icon" }) }),
                /* @__PURE__ */ jsxs(ListItemContentWrapper, { isHidden: showRecollectFactoringConfirm, children: [
                  /* @__PURE__ */ jsx(ListItemTextWrapper, { children: /* @__PURE__ */ jsx(StyledListItemText, { variant: "Lato Emphasized 2", children: labels.anamnesis.steps.factoringAgreement }) }),
                  factoring.filledAt && /* @__PURE__ */ jsx(Time, { variant: "Lato Caption", children: formatDefault(factoring.filledAt) })
                ] }),
                /* @__PURE__ */ jsxs(ListItemSecondaryAction, { children: [
                  showFactoringRecollectIcon && /* @__PURE__ */ jsx(StyledTooltip, { title: labels.anamnesis.retriggerDocument, children: /* @__PURE__ */ jsx(StyledIconButton, { onClick: () => setShowRecollectFactoringConfirm(true), "data-testid": "recollect-btn", children: /* @__PURE__ */ jsx(RefreshIcon, {}) }) }),
                  showRecollectFactoringConfirm && /* @__PURE__ */ jsxs(Fragment, { children: [
                    /* @__PURE__ */ jsx(StyledButton, { onClick: () => setShowRecollectFactoringConfirm(false), children: labels.anamnesis.recollectCancel }),
                    /* @__PURE__ */ jsx(StyledButton, { contained: true, onClick: onRecollectFactoringAgreement, children: labels.anamnesis.recollectConfirm })
                  ] })
                ] })
              ] })
            ]
          }
        ),
        !areDocumentsCompleted && /* @__PURE__ */ jsxs(
          StyledList,
          {
            subheader: /* @__PURE__ */ jsx(StyledListSubheader, { disableGutters: true, disableSticky: true, children: labels.anamnesis.generateMissingDocuments }),
            children: [
              canSendSMS(patientPhoneNumber) && /* @__PURE__ */ jsxs(
                StyledListItemButton,
                {
                  onClick: !isSmsSent ? onSendSms : void 0,
                  isClickable: !isSmsSent,
                  disabled: !isDocumentActionsActive,
                  disableRipple: true,
                  disableGutters: true,
                  "data-testid": "send-sms-button",
                  children: [
                    !isSmsSent ? /* @__PURE__ */ jsxs(ListItemButtonText, { disabled: !isDocumentActionsActive, variant: "Lato Link 2", children: [
                      /* @__PURE__ */ jsx(StyledMessageBubbleIcon, {}),
                      labels.anamnesis.sendLink
                    ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
                      /* @__PURE__ */ jsxs(SmsSentText, { variant: "Lato Button", children: [
                        /* @__PURE__ */ jsx(SmsSentIcon, {}),
                        labels.anamnesis.smsSent
                      ] }),
                      /* @__PURE__ */ jsx(
                        SendSmsAgainButton,
                        {
                          variant: "borderless",
                          startIcon: /* @__PURE__ */ jsx(RefreshRoundedIcon, {}),
                          onClick: onSendSms,
                          disableRipple: true,
                          children: labels.anamnesis.sendSmsAgain
                        }
                      )
                    ] }),
                    /* @__PURE__ */ jsx(ListItemSecondaryAction, { children: loading.isSendingSmsLoading && /* @__PURE__ */ jsx(CircularProgress, { size: 14, thickness: 6, "data-testid": "sms-spinner" }) })
                  ]
                }
              ),
              !generatedCode ? /* @__PURE__ */ jsxs(
                StyledListItemButton,
                {
                  onClick: onGenerateCode,
                  isClickable: !generatedCode,
                  disabled: !isDocumentActionsActive,
                  disableRipple: true,
                  disableGutters: true,
                  "data-testid": "generate-code-button",
                  children: [
                    /* @__PURE__ */ jsxs(ListItemButtonText, { disabled: !isDocumentActionsActive, variant: "Lato Link 2", children: [
                      /* @__PURE__ */ jsx(StyledTabletIcon, {}),
                      labels.anamnesis.generateTabletCode
                    ] }),
                    /* @__PURE__ */ jsx(ListItemSecondaryAction, { children: loading.isGeneratingCodeLoading && /* @__PURE__ */ jsx(CircularProgress, { size: 14, thickness: 6, "data-testid": "code-spinner" }) })
                  ]
                }
              ) : /* @__PURE__ */ jsx(GeneratedCodeListItem, { disableGutters: true, disablePadding: true, "data-testid": "generated-code", children: /* @__PURE__ */ jsxs(GeneratedCodeText, { variant: "Lato H5 Bold", children: [
                /* @__PURE__ */ jsx(StyledTabletIcon, {}),
                generatedCode
              ] }) })
            ]
          }
        )
      ] })
    }
  );
};
export {
  AnamnesisStatusPopover as default
};
